import { useMemo } from 'react';

import type { FieldConfidenceResponse } from '../api';
import type { FieldEditState } from './useFieldEdit';

export interface FieldAggregates {
  ok: number;
  warning: number;
  critical: number;
  edited: number;
  total: number;
}

/**
 * Flattens a (possibly nested) confidence list into the entries that count toward the
 * aggregate. Group entries themselves are not counted — their children are. Tables stay
 * as a single leaf because today's data model carries one status/score per table, not
 * per row (per-row confidence is a future enrichment).
 */
function flattenLeaves(
  fields: ReadonlyArray<FieldConfidenceResponse>,
): FieldConfidenceResponse[] {
  const leaves: FieldConfidenceResponse[] = [];
  for (const field of fields) {
    if (field.children != null && field.children.length > 0) {
      leaves.push(...field.children);
    } else {
      leaves.push(field);
    }
  }
  return leaves;
}

export function computeAggregates(
  fields: ReadonlyArray<FieldConfidenceResponse>,
  edits: Record<string, FieldEditState>,
): FieldAggregates {
  const leaves = flattenLeaves(fields);
  let ok = 0;
  let warning = 0;
  let critical = 0;
  let edited = 0;
  for (const field of leaves) {
    switch (field.status) {
      case 'ok':
        ok += 1;
        break;
      case 'warning':
        warning += 1;
        break;
      case 'critical':
        critical += 1;
        break;
    }
    if (edits[field.name]?.manuallyEdited === true) {
      edited += 1;
    }
  }
  return { ok, warning, critical, edited, total: leaves.length };
}

/**
 * Returns true when a row needs the operator's attention. Used by the smart-auto
 * accordion heuristic (Group and Table headers open by default when this is true)
 * and by the HUD to highlight buckets that have non-zero counts.
 */
export function hasIssue(
  field: FieldConfidenceResponse,
  edits: Record<string, FieldEditState>,
): boolean {
  if (field.status !== 'ok') {
    return true;
  }
  if (edits[field.name]?.manuallyEdited === true) {
    return true;
  }
  if (field.children != null) {
    return field.children.some((child) => hasIssue(child, edits));
  }
  return false;
}

export function useFieldAggregates(
  fields: ReadonlyArray<FieldConfidenceResponse>,
  edits: Record<string, FieldEditState>,
): FieldAggregates {
  return useMemo(() => computeAggregates(fields, edits), [fields, edits]);
}
